import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", txn: null, attempts: 0 });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", txn: null, attempts: 0 });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ phase: "paid", txn: data, attempts: attemptsRef.current });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", txn: data, attempts: attemptsRef.current });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", txn: data, attempts: attemptsRef.current });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch {
        if (cancelled) return;
        setState({ phase: "error", txn: null, attempts: attemptsRef.current });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="max-w-2xl mx-auto px-6 pt-32 pb-24 text-center">
      {state.phase === "polling" && (
        <>
          <Clock size={56} style={{ color: "var(--brass)", margin: "0 auto 1.5rem" }} />
          <p className="eyebrow mb-4">Confirming payment</p>
          <h1 className="font-display text-5xl md:text-6xl mb-6 leading-[0.95]">
            One moment…
          </h1>
          <p className="text-base" style={{ color: "var(--ink-muted)" }}>
            We are verifying your transaction on Stripe&apos;s network.
          </p>
        </>
      )}
      {state.phase === "paid" && (
        <>
          <CheckCircle2 size={56} style={{ color: "var(--brass)", margin: "0 auto 1.5rem" }} />
          <p className="eyebrow mb-4">Payment Received</p>
          <h1 className="font-display text-5xl md:text-6xl mb-6 leading-[0.95]">
            Thank you, <em style={{ color: "var(--brass)" }}>partner</em>.
          </h1>
          <p className="text-base leading-relaxed mb-8" style={{ color: "var(--ink-muted)" }}>
            Your payment of{" "}
            <strong style={{ color: "var(--ink)" }}>
              ${(((state.txn?.amount_total_cents || 0) / 100) || state.txn?.amount || 0).toLocaleString()}
            </strong>{" "}
            for <em>{state.txn?.metadata?.label}</em> has been received. A partner advisor will reach
            out within one business day with onboarding steps.
          </p>
          <div className="p-6 mb-8 text-left max-w-md mx-auto"
            style={{ border: "1px solid var(--hairline)", background: "var(--surface)" }}
            data-testid="payment-receipt">
            <p className="eyebrow mb-3">Receipt</p>
            <Row label="Reference" value={sessionId?.slice(-12)} />
            <Row label="Status" value="Paid" highlight />
            <Row label="Rail" value={state.txn?.metadata?.rail || "Stripe"} />
            <Row label="Package" value={state.txn?.metadata?.label || "—"} />
          </div>
          <Link to="/" className="btn-brass" style={{ justifyContent: "center" }}>
            Back to home <ArrowRight size={14} />
          </Link>
        </>
      )}
      {(state.phase === "timeout" || state.phase === "expired" || state.phase === "error") && (
        <>
          <AlertTriangle size={56} style={{ color: "#cc7755", margin: "0 auto 1.5rem" }} />
          <p className="eyebrow mb-4">{state.phase === "expired" ? "Session expired" : "Still processing"}</p>
          <h1 className="font-display text-4xl md:text-5xl mb-6 leading-[0.95]">
            We&apos;re verifying your transaction.
          </h1>
          <p className="text-base leading-relaxed mb-8" style={{ color: "var(--ink-muted)" }}>
            If your card was charged, we will email a receipt shortly. You may also contact{" "}
            <a href="mailto:support@wealthfirstinc.tv" style={{ color: "var(--brass)" }}>
              support@wealthfirstinc.tv
            </a>{" "}
            for immediate assistance.
          </p>
          <Link to="/" className="btn-brass" style={{ justifyContent: "center" }}>
            Back to home <ArrowRight size={14} />
          </Link>
        </>
      )}
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex justify-between items-baseline mb-2 last:mb-0">
      <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--ink-muted)" }}>{label}</span>
      <span className="text-sm" style={{ color: highlight ? "var(--brass)" : "var(--ink)" }}>{value}</span>
    </div>
  );
}
